import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

/**
 * 旧URL構造から新URL構造へのリダイレクト
 *
 * 旧URL → 新URL:
 * - /?tab=stillImage → /studio/image
 * - /?tab=video → /studio/video
 * - /?tab=scenario → /studio/video
 * - /?scenario_id=xxx → /studio/video/xxx
 * - /?material_id=xxx&folder_type=temporary → /materials/personal?detail=xxx
 * - /?material_id=xxx&folder_type=cloud → /materials/shared?detail=xxx
 *
 * パラメータがない場合は fallback を表示（指定がなければ /studio へリダイレクト）
 */
type Props = {
  fallback?: ReactNode;
};

export function LegacyUrlRedirect({ fallback }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const scenarioId = searchParams.get('scenario_id');
    const materialId = searchParams.get('material_id');
    const folderType = searchParams.get('folder_type');
    const mode = searchParams.get('mode');

    // scenario_id パラメータがある場合
    if (scenarioId) {
      navigate(`/studio/video/${scenarioId}`, { replace: true });
      return;
    }

    // material_id + folder_type パラメータがある場合
    if (materialId && folderType) {
      const folder = folderType === 'temporary' ? 'personal' : 'shared';
      navigate(`/materials/${folder}?detail=${materialId}`, { replace: true });
      return;
    }

    // tab パラメータがある場合
    if (tab === 'video' || tab === 'scenario') {
      navigate('/studio/video', { replace: true });
      return;
    }

    if (tab === 'stillImage') {
      if (mode === 'prompt') {
        navigate('/studio/image/prompt', { replace: true });
      } else {
        navigate('/studio/image', { replace: true });
      }
      return;
    }

    // パラメータがない場合
    if (fallback) {
      setShowFallback(true);
    } else {
      navigate('/studio', { replace: true });
    }
  }, [searchParams, navigate, fallback]);

  if (showFallback) {
    return <>{fallback}</>;
  }

  return null;
}
